'use client';

import { useLocale, useTranslations } from 'next-intl';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { formatDateTime } from '@/shared/lib/utils';

interface AlbumDuplicateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ISO timestamp of the existing album, if known. */
  generatedAt?: string;
  /** "Use existing" — dismiss without generating. */
  onUseExisting: () => void;
  /** "Create new" — force a fresh generation. */
  onGenerateNew: () => void;
}

/**
 * Confirmation shown when the album generate request returns `duplicate_exists`
 * (an identical album already exists). Shared by both the single-column panel
 * and the two-column inspector so the prompt appears in every layout.
 *
 * Styling leans iOS: a dimmed scrim over a borderless white card, rather than
 * the app-default translucent bordered alert. The "created …" date is formatted
 * with the active `next-intl` locale (not the browser default), so ru renders
 * as ru instead of en-US.
 */
export function AlbumDuplicateDialog({
  open,
  onOpenChange,
  generatedAt,
  onUseExisting,
  onGenerateNew,
}: AlbumDuplicateDialogProps) {
  const t = useTranslations('album');
  const locale = useLocale();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('duplicateExists.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {generatedAt
              ? t('duplicateExists.message', {
                  date: formatDateTime(generatedAt, locale),
                })
              : t('duplicateExists.messageNoDate')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onUseExisting}>
            {t('duplicateExists.useExisting')}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onGenerateNew}>
            {t('duplicateExists.generateNew')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
